import api from '../../../../../../../../services/api'

/**
 * Utility functions for handling image fetching with CORS support
 */

/**
 * Fetches an image and converts it to base64, with CORS error handling
 * @param {string} imageUrl - The URL of the image to fetch
 * @param {Object} options - Additional options
 * @returns {Promise<string>} - Base64 data URL of the image
 */
export const fetchImageAsBase64 = async (imageUrl, options = {}) => {
  try {
    // First try with CORS mode
    const response = await fetch(imageUrl, {
      mode: 'cors',
      credentials: 'omit',
      ...options
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const blob = await response.blob()
    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = () => reject(new Error('Failed to read blob'))
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('CORS error fetching image:', error)
    
    // If CORS fails, try to create a proxy URL or return the original URL
    if (options.fallbackToUrl) {
      return imageUrl
    }
    
    throw error
  }
}

/**
 * Creates a proxy URL for images that might have CORS issues
 * @param {string} imageUrl - The original image URL
 * @param {string} proxyBaseUrl - The base URL for the proxy service
 * @returns {string} - The proxy URL
 */
export const createProxyUrl = (imageUrl, proxyBaseUrl = '/api/proxy/image') => {
  const encodedUrl = encodeURIComponent(imageUrl)
  return `${proxyBaseUrl}?url=${encodedUrl}`
}

/**
 * Checks if an image URL is likely to have CORS issues
 * @param {string} imageUrl - The image URL to check
 * @returns {boolean} - True if the URL might have CORS issues
 */
export const hasPotentialCorsIssues = (imageUrl) => {
  if (!imageUrl) return false
  
  try {
    const url = new URL(imageUrl)
    // Check if it's an external domain (not same origin)
    const currentOrigin = window.location.origin
    return url.origin !== currentOrigin
  } catch (error) {
    // If URL parsing fails, assume it might have CORS issues
    return true
  }
}

/**
 * Alternative method to handle CORS using HTML5 canvas
 * This method creates a canvas, draws the image, and exports as base64
 * @param {string} imageUrl - The image URL
 * @returns {Promise<string>} - Base64 data URL
 */
export const convertImageToBase64ViaCanvas = (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        
        // Set canvas size to match image
        canvas.width = img.width
        canvas.height = img.height
        
        // Draw image to canvas
        ctx.drawImage(img, 0, 0)
        
        // Convert to base64
        const dataUrl = canvas.toDataURL('image/png')
        resolve(dataUrl)
      } catch (error) {
        reject(error)
      }
    }
    
    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }
    
    // Add cache buster to avoid cached CORS errors
    const separator = imageUrl.includes('?') ? '&' : '?'
    img.src = `${imageUrl}${separator}_cb=${Date.now()}`
  })
}

/**
 * Fetches an image with multiple fallback strategies
 * @param {string} imageUrl - The image URL
 * @param {Object} options - Options for fetching
 * @returns {Promise<string>} - Base64 data URL or original URL
 */
export const fetchImageWithFallback = async (imageUrl, options = {}) => {
  const {
    useProxy = true,
    useCanvas = true,
    fallbackToUrl = true,
    showWarning = true
  } = options

  try {
    // Try direct fetch first
    return await fetchImageAsBase64(imageUrl, { fallbackToUrl: false })
  } catch (error) {
    console.warn('Direct fetch failed, trying canvas method...', error)
    
    if (useCanvas) {
      try {
        // Try using canvas method
        return await convertImageToBase64ViaCanvas(imageUrl)
      } catch (canvasError) {
        console.warn('Canvas method failed, trying proxy...', canvasError)
      }
    }
    
    if (useProxy) {
      try {
        // Try using the proxy service
        return await api.proxy.proxyImageAsBase64(imageUrl)
      } catch (proxyError) {
        console.error('Proxy fetch also failed:', proxyError)
      }
    }
    
    // Final fallback: return the original URL
    if (fallbackToUrl) {
      if (showWarning) {
        const { message } = require('antd')
        message.warning('Image loaded with limited functionality due to CORS restrictions.')
      }
      return imageUrl
    }
    
    throw error
  }
} 